import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import {
  alCambiarEstadoDeInstalacion,
  hayEventoDeInstalacion,
  lanzarInstalacion,
  seInstaloEnEstaSesion,
} from '../../utils/install-prompt.store';

/**
 * Cómo puede instalar el usuario que está mirando la página.
 *
 *  - `nativo`: el navegador nos deja abrir su propio diálogo (Chrome y Edge).
 *  - `ios`, `safari-mac`, `chromium`, `firefox`: no hay forma de dispararlo
 *    desde la página, así que se explican los pasos.
 *  - `oculto`: ya está instalada, o el navegador no permite instalar nada.
 */
type ModoInstalacion =
  | 'nativo'
  | 'ios'
  | 'safari-mac'
  | 'chromium'
  | 'firefox'
  | 'oculto';

@Component({
  selector: 'app-install-app-button',
  templateUrl: './install-app-button.component.html',
  styleUrls: ['./install-app-button.component.sass'],
})
export class InstallAppButtonComponent implements OnInit, OnDestroy {
  modo: ModoInstalacion = 'oculto';

  /**
   * El texto nombra el aparato que la persona tiene en la mano y lo que gana,
   * no la acción técnica. Se resuelve por dispositivo y no por ancho de
   * pantalla: un celular grande leería "computadora" con una consulta de
   * medios.
   *
   * `detalle` no entra en la barra —ahí se recortaba— sino en el diálogo.
   */
  titulo = '';
  detalle = '';

  private desuscribir: (() => void) | null = null;

  ngOnInit(): void {
    this.armarTextos();
    this.recalcularModo();
    // El evento de instalación puede llegar después de pintar el botón.
    this.desuscribir = alCambiarEstadoDeInstalacion(() =>
      this.recalcularModo(),
    );
  }

  ngOnDestroy(): void {
    this.desuscribir?.();
  }

  get visible(): boolean {
    return this.modo !== 'oculto';
  }

  /**
   * Cuando no hay nada que ofrecer se apaga el host, no sólo el botón. El
   * login lo trata como un flex item con margen inferior y los márgenes de un
   * flex item no colapsan: esconder únicamente el botón dejaba ese margen como
   * aire muerto entre el logo y "Iniciar sesión" a todo el que ya tenía la app
   * instalada.
   */
  @HostBinding('class.install-app-oculto')
  get oculto(): boolean {
    return !this.visible;
  }

  private recalcularModo(): void {
    if (this.yaEstaInstalada()) {
      this.modo = 'oculto';
      return;
    }
    if (hayEventoDeInstalacion()) {
      this.modo = 'nativo';
      return;
    }
    if (this.esIOS()) {
      this.modo = 'ios';
      return;
    }
    if (this.esSafariDeEscritorio()) {
      this.modo = 'safari-mac';
      return;
    }
    if (this.esFirefox()) {
      this.modo = 'firefox';
      return;
    }
    if (this.esChromium()) {
      // Chrome y Edge instalan desde su menú aunque no nos den el evento.
      this.modo = 'chromium';
      return;
    }
    this.modo = 'oculto';
  }

  // ==================== Textos ====================

  private armarTextos(): void {
    if (this.esEscritorio()) {
      this.titulo = 'Instala Aguacate en tu computadora';
      this.detalle = 'Se abre como una app, sin buscar la página';
      return;
    }
    const aparato = this.esTablet() ? 'tablet' : 'celular';
    this.titulo = `Instala Aguacate en tu ${aparato}`;
    this.detalle = 'Queda como un ícono más, sin pasar por la tienda';
  }

  /** Sin pantalla táctil no hay pantalla de inicio donde dejar el ícono. */
  private esEscritorio(): boolean {
    return !this.esIOS() && navigator.maxTouchPoints === 0;
  }

  private esTablet(): boolean {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 768px)').matches
    );
  }

  // ==================== Detección ====================

  /**
   * Si la página ya se está viendo dentro de la app instalada no tiene sentido
   * ofrecer instalarla. `navigator.standalone` es el equivalente de iOS, que no
   * soporta la consulta de medios.
   */
  private yaEstaInstalada(): boolean {
    if (seInstaloEnEstaSesion()) return true;
    const comoApp =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches;
    const enIOS = (window.navigator as any).standalone === true;
    return comoApp || enIOS;
  }

  /** iPad con iPadOS se hace pasar por Mac: se lo delata el soporte táctil. */
  private esIOS(): boolean {
    const ua = navigator.userAgent;
    const iPadOS =
      navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return /iPad|iPhone|iPod/.test(ua) || iPadOS;
  }

  private esSafariDeEscritorio(): boolean {
    const ua = navigator.userAgent;
    return /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|Firefox/.test(ua);
  }

  private esFirefox(): boolean {
    return /Firefox|FxiOS/.test(navigator.userAgent);
  }

  private esChromium(): boolean {
    return /Chrome|Chromium|CriOS|Edg|OPR/.test(navigator.userAgent);
  }

  // ==================== Acción ====================

  async instalar(): Promise<void> {
    if (this.modo === 'nativo') {
      const acepto = await lanzarInstalacion();
      if (!acepto) {
        // Rechazar no es un error: el navegador no vuelve a darnos el evento,
        // así que a partir de acá se ofrecen los pasos manuales.
        this.recalcularModo();
      }
      return;
    }
    this.mostrarPasos();
  }

  private mostrarPasos(): void {
    Swal.fire({
      html: this.pasosSegunModo(),
      showConfirmButton: true,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#1C593D',
      width: 420,
    });
  }

  private pasosSegunModo(): string {
    // El nombre va con el tratamiento de la marca —"Aguacate" sólido y
    // "Wallet" liviano, en los dos verdes— igual que en el panel del login:
    // instalar algo pide reconocer de entrada quién lo firma.
    const encabezado = `
      <div style="text-align:center;margin-bottom:16px;padding-bottom:16px;border-bottom:1px solid #e5e7eb;">
        <img src="assets/icons/pwa/icon-192.png" alt="Aguacate Wallet"
             style="width:64px;height:64px;border-radius:16px;box-shadow:0 6px 20px rgba(28,89,61,.18);" />
        <p style="font-size:1.3rem;font-weight:800;color:#1C593D;letter-spacing:.5px;margin:12px 0 0;line-height:1.2;">
          Aguacate<span style="font-weight:300;color:#059669;">Wallet</span>
        </p>
        <p style="font-size:.82rem;font-weight:600;color:#6b7280;margin:8px 0 0;">
          ${this.titulo}
        </p>
        <p style="font-size:.78rem;color:#9ca3af;margin:4px 0 0;line-height:1.45;">
          ${this.detalle}
        </p>
      </div>`;

    const pasos = this.listaDePasos();
    const nota = this.notaSegunModo();

    return `
      ${encabezado}
      <ol style="text-align:left;margin:0;padding-left:20px;display:flex;flex-direction:column;gap:8px;font-size:0.88rem;color:#374151;line-height:1.5;">
        ${pasos.map((p) => `<li>${p}</li>`).join('')}
      </ol>
      ${nota}`;
  }

  private listaDePasos(): string[] {
    switch (this.modo) {
      case 'ios':
        return [
          'Tocá el botón <b>Compartir</b> en la barra de Safari (el cuadrado con la flecha hacia arriba).',
          'Bajá en la lista y elegí <b>Agregar a inicio</b>.',
          'Tocá <b>Agregar</b>. El ícono te queda entre tus aplicaciones.',
        ];
      case 'safari-mac':
        return [
          'En la barra de menú de Safari, abrí <b>Archivo</b>.',
          'Elegí <b>Agregar al Dock</b>.',
          'Confirmá el nombre y listo: queda como una aplicación más.',
        ];
      case 'chromium':
        return [
          'Abrí el menú del navegador (los tres puntos, arriba a la derecha).',
          'Elegí <b>Instalar aplicación</b> o <b>Agregar a la pantalla de inicio</b>.',
          'Confirmá y el acceso directo queda en tu dispositivo.',
        ];
      case 'firefox':
        return [
          'En el celular: menú del navegador → <b>Agregar a la pantalla de inicio</b>.',
          'En la computadora, Firefox no permite instalar sitios. Podés abrir Aguacate en Chrome o Edge e instalarlo desde ahí.',
        ];
      default:
        return [
          'Abrí el menú de tu navegador y buscá la opción de instalar o agregar a la pantalla de inicio.',
        ];
    }
  }

  /** El aviso de iOS evita el reclamo de "me pide la clave otra vez". */
  private notaSegunModo(): string {
    if (this.modo !== 'ios' && this.modo !== 'safari-mac') return '';
    return `
      <div style="display:flex;gap:8px;align-items:flex-start;background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:10px 12px;margin-top:14px;text-align:left;">
        <span style="font-size:1rem;line-height:1;">💡</span>
        <span style="font-size:0.8rem;color:#92400e;line-height:1.45;">
          La primera vez que abras el ícono vas a tener que iniciar sesión de nuevo:
          la aplicación guarda tu sesión aparte de la del navegador.
        </span>
      </div>`;
  }
}
